import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Container, Row, Col, Card, CardHeader, CardBody, Button } from 'reactstrap';
import { useAlertPlus } from '../../../hooks';
import AlertModal from '../../../components/AlertModal';
import Form from './Form';
import config from 'config';
import { ApiService } from '../../../services';
import { getPreviewSrc, encodeFileName } from '../../../helpers/filesPreview';

import './Edit.scss';

export interface Trabajador {
  matricula: string;
  esDirector: boolean;
}

export interface ConsultorioRequest {
  id: string | undefined;
  matriculado: {
    matricula: number;
    nombre: string;
    apellido: string;
    delegacion: string;
  }

  nombre: string;
  prefijo_telefono: string;
  telefono: string;
  calle: string;
  numero: string;
  piso: string;
  departamento: string;
  localidad: string;
  partido: string;
  codigo_postal: string;

  tipo_propietario: string;
  nombre_propietario: string;
  cuit_propietario: string;

  cantidad_gabinetes: number;
  director_tecnico: number;
  numero_habilitacion: string;
  numero_expediente: string;
  anio_expediente: string;
  numero_gestion: string;
  habilitacion_temporal: boolean;

  consultorio_ck_id: string;

  motivo_rechazo: string;

  state_id: string;

  trabajadores: Trabajador[];

  attachs: RequestAttachment[];
  horarios_atencion: HorariosAtencion[];
}

interface HorariosAtencion {
  horaInicio: string;
  horaFin: string;
  diaSemana: string;
}

export interface RequestAttachment {
  id: number;
  request_id: number;
  request_type: string;
  attachment_type: string;
  url: string;
  created_at: string;
}

export interface GalleryItem {
  src: string;
  downloadUrl: string;
  alt: string;
}

function Edit() {
  const { id } = useParams();

  const [data, setData] = useState<ConsultorioRequest>({
    id: id,
    matriculado: {
      matricula: 0,
      nombre: '',
      apellido: '',
      delegacion: '',
    },

    nombre: '',
    prefijo_telefono: '',
    telefono: '',
    calle: '',
    numero: '',
    piso: '',
    departamento: '',
    localidad: '',
    partido: '',
    codigo_postal: '',

    tipo_propietario: '',
    nombre_propietario: '',
    cuit_propietario: '',

    cantidad_gabinetes: 0,
    director_tecnico: 0,
    numero_habilitacion: '',
    numero_expediente: '',
    anio_expediente: '',
    numero_gestion: '',
    habilitacion_temporal: false,

    consultorio_ck_id: '',

    motivo_rechazo: '',

    state_id: '',

    trabajadores: [],

    attachs: [],
    horarios_atencion: [],
  });

  const [gallery, setGallery] = useState<GalleryItem[]>([]);
  const galleryContainer = useRef(null);

  const [isLoading, setIsLoading] = useState(true);
  const [alertShow, alertMessage, alertCallback, alert] = useAlertPlus('');
  const [stateText, setStateText] = useState('');

  useEffect(() => {
    if (data.state_id != '') {
      switch (+data.state_id) {
        case 1:
          setStateText('Solicitado');
          break;
        case 2:
          setStateText('Aceptado');
          break;
        case 3:
          setStateText('Rechazado');
          break;
        case 4:
          setStateText('Enviado a Inspección');
          break;
        case 5:
          setStateText('Enviado Ministerio');
          break;
        case 6:
          setStateText('En Corrección');
          break;
        case 7:
          setStateText('Habilitado');
          break;
        default:
          setStateText('');
          break;
      }
    }
  }, [data.state_id]);

  const ATTACHMENTS_CONFIG = [
    { key: 'constancia_pagos', alt: 'Constancia de Pagos' },
    { key: 'solicitud_habilitacion', alt: 'Solicitud de Habilitación' },
    { key: 'declaracion_aparatologia', alt: 'Declaración Jurada de Aparatología' },
    { key: 'declaracion_patogenicos', alt: 'Declaración Jurada de Patogénicos' },
    { key: 'plano_municipal', alt: 'Plano Municipal' },
    { key: 'contrato_alquiler', alt: 'Contrato de Alquiler' },
    { key: 'titulos_grado', alt: 'Título de Grado' },
    { key: 'titulos_grado_dorso', alt: 'Dorso Título de Grado' },
    { key: 'constancia_cuit', alt: 'Constancia de CUIT' },
    { key: 'croquis_consultorio', alt: 'Croquis del Consultorio' },
    { key: 'contrato_comodato', alt: 'Escritura Contrato Comodato' },
    { key: 'estatuto_societario', alt: 'Estatuto Societario' },
  ] as const;

  useEffect(() => {
    ApiService.getOne('matriculates/consultorios-request', id)
      .then(({ payload }) => {
        setData(payload);

        const attachs = ATTACHMENTS_CONFIG.flatMap(({ key, alt }) => {
          if (!payload.attachs?.length) return [];

          const filtered = payload.attachs
            .filter((a: RequestAttachment) => a.attachment_type.startsWith(key))
            .sort((a: RequestAttachment, b: RequestAttachment) => {
              const indexA = parseInt(a.attachment_type.split('_').pop() || '0');
              const indexB = parseInt(b.attachment_type.split('_').pop() || '0');
              return indexA - indexB;
            });

          let i = 0;
          return filtered.map((a: RequestAttachment) => {
            const encodedPath = encodeFileName(a.url);
            i++;

            return {
              src: getPreviewSrc(encodedPath),
              downloadUrl: `${config.baseUrl}/${encodedPath}`,
              // @ts-ignore
              alt: `${alt} ${i}`,
            };
          });
        });

        setGallery(attachs);
      })
      .finally(() => setIsLoading(false));
  }, [id]);

  const onChangeState = async (newState: number) => {
    try {
      setIsLoading(true);

      const headers: HeadersInit = {
        ...(localStorage.getItem('accessToken') && {
          Authorization: localStorage.getItem('accessToken') as string,
        }),
      };

      // Preparamos los datos
      const container = document.querySelector('#actions-row') as HTMLElement;
      const formData = new FormData();

      if (container) {
        const elements = container.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
          'input[name], textarea[name], select[name]',
        );

        elements.forEach(el => {
          if (el instanceof HTMLInputElement && el.type === 'checkbox') {
            formData.append(el.name, String(el.checked));
          } else {
            formData.append(el.name, el.value);
          }
        });
      }

      // agregar el state
      formData.append('state', String(newState));

      if (newState == 2) {
        // agregar los trabajadores
        formData.append('trabajadores', JSON.stringify(data.trabajadores));
      }

      const res = await fetch(
        `${config.baseUrl}/matriculates/consultorios-request/${ id }/change-state`,
        {
          method: 'POST',
          headers,
          body: formData,
        },
      );

      const response = await res.json();

      if (response?.status !== 'ok') {
        console.info(response);

        if (response.payload?.message) {
          alert(response.payload.message);
          return;
        }

        alert('Ha ocurrido un error al enviar al servidor, por favor intente nuevamente.');
        return;
      }

      alert('Se guardó correctamente la resolución.', () => {
        window.location.reload();
      });
    } catch (err) {
      console.error(err);
      alert('Ha ocurrido un error al guardar la resolución, por favor intente nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="header bg-gradient-info pb-8 pt-5 pt-md-8">
        <Container fluid>
          <div className="header-body"></div>
        </Container>
      </div>
      <Container className="mt--7" fluid>
        <Card className="bg-secondary shadow">
          <CardHeader>
            <Row>
              <Col md="8">
                <h3 className="mb-0">Solicitud de Consultorio #{ id } - { stateText }</h3>
              </Col>
              <Col md="4" className="text-right">
                <Link to='/admin/matriculates/requests'>
                  <Button color="primary" size="md">Volver</Button>
                </Link>
              </Col>
            </Row>
          </CardHeader>
          <CardBody>
            <Form
              data={ data }
              setData={ setData }
              gallery={ gallery }
              galleryContainer={ galleryContainer }
              isLoading={ isLoading }
              onChangeState={ onChangeState }
            />
          </CardBody>
        </Card>
      </Container>
      <AlertModal isOpen={ alertShow } message={ alertMessage } onClose={ alertCallback } />
    </>
  );
}

export default Edit;
